import type { SvgIconComponent } from '@mui/icons-material';
import type { NavigateFunction } from 'react-router-dom';
import type { TFunction } from 'i18next';

export type SlashCommandGroup = 'actions' | 'related' | 'history' | 'recent' | 'create' | 'navigation' | 'utilities';

/**
 * Groups whose commands are built from runtime state rather than the static registry.
 *
 * They must stay out of the command MRU. Their ids are bound to one record, so recording
 * them both spends the recent slots on entries that cannot resolve elsewhere and — because
 * "Recent" is deduplicated against every other group — silently moves the entry out of the
 * section it belongs to. Both of those read to the user as history losing its entries.
 */
export function isDynamicGroup(group: SlashCommandGroup): boolean {
  return group === 'actions' || group === 'related' || group === 'history';
}

/**
 * Resolves a localized string, either from a raw value or from an i18n key.
 * `key` takes precedence over `value` when both are present, but falls back to
 * `value` if the key is missing in the active language.
 */
export interface LocalizedText {
  /** Raw fallback text (used when no key is provided, or as default for a missing key). */
  value?: string;
  /** i18n key (ABP-style or dotted); looked up via t(). */
  key?: string;
  /** Interpolation args passed to t(). */
  args?: Record<string, string>;
  /**
   * Interpolation args whose values are themselves i18n keys (e.g. `Menu:Finance.PlanView`),
   * looked up before interpolation. Falls back to the same-named entry in `args`, so a command
   * keeps its English text when the key is missing.
   */
  argKeys?: Record<string, string>;
}

/**
 * A command that can be run from the global Cmd+K palette when the user types a "/" prefix.
 */
export interface SlashCommand {
  /** Stable, language-agnostic id. Used for MRU history and deduplication.
   *  Conventions: "nav.<module>.<itemId>", "create.<module>.<qaId>", "util.<name>". */
  id: string;
  /** Display title (localized at render). */
  title: LocalizedText;
  /** Optional right-side hint (path, module, etc.). */
  subtitle?: LocalizedText;
  /** Which group the command belongs to (controls section + ordering). */
  group: Exclude<SlashCommandGroup, 'recent'>;
  /** Raw keyword aliases for fuzzy matching (language-agnostic). */
  keywords?: string[];
  /** Optional i18n key whose value is a comma-separated list of localized keyword aliases. */
  localizedKeywordsKey?: string;
  /** Icon shown in the list. */
  icon?: SvgIconComponent;
  /** Permission(s) required to see the command. If an array, all must be granted. */
  permission?: string | string[];
  /** ABP feature required to see the command (hidden when it evaluates to "false"). */
  feature?: string;
  /** Handler invoked when the user runs the command. */
  run: (ctx: CommandContext) => void | Promise<void>;
}

/**
 * Runtime context passed to every command's `run()` function. The dialog composes this
 * from hooks/contexts so individual command files stay pure.
 */
export interface CommandContext {
  navigate: NavigateFunction;
  closeDialog: () => void;
  logout: () => Promise<void>;
  /** Write a user preference (see USER_PREF_KEYS). */
  updatePreference: (key: string, value: string) => void;
  currentTheme: 'light' | 'dark' | 'system';
  currentLanguage: string;
  t: TFunction;
  /**
   * Report a failure instead of closing. Commands that only navigate never need this;
   * commands that mutate do — the palette is an overlay with no owning page to hand a
   * validation error to, so without this the failure would vanish silently on close.
   */
  reportError?: (error: unknown) => void;
}

/** Result of running the matcher against a single command. */
export interface MatchedCommand {
  command: SlashCommand;
  /** Higher is better; 0 means "included because input is empty"; <0 would be excluded (never returned). */
  score: number;
  /** Localized title used for display, already resolved. */
  localizedTitle: string;
  /** Localized subtitle (may be empty). */
  localizedSubtitle: string;
}
